import { Box, Button, DropdownOption, TextField } from "@modernatx-internal/component-library";
import { useRouter } from "next/router";
import React from "react";

import { useExperience } from "@/context/ExperienceContext";
import { useLocationSearch } from "@/finder/hooks/V2/useLocationSearch";
import { useExitModal } from "@/hooks/useExitModal";

import type { ContentComponentProps, LocationFinderContent } from "../types";

export const LocationFinder: React.FC<ContentComponentProps<LocationFinderContent>> = ({
  button,
  clientGeolocationErrorMessages,
  detectLocationDropdownLabel,
  finderLocation = "/finder",
  forceExitModalReason,
  input,
  layout = "horizontal",
  product = ""
}) => {
  const {
    brand = "secondary",
    icon,
    iconOnly = false,
    sx: buttonSx,
    title: buttonTitle,
    variant = "filled"
  } = button;
  const { expandedMode } = useExperience();
  const { openExitModalForLink } = useExitModal();
  const [searchValue, searchValueSet] = React.useState("");
  const optionSelected = React.useRef<DropdownOption | null>(null);
  const router = useRouter();
  const formRef = React.useRef<HTMLFormElement>(null);

  // Route selection through form submit so analytics still see the submit event.
  const handleSubmit = React.useCallback(() => {
    formRef.current?.dispatchEvent(
      new Event("submit", {
        bubbles: true,
        cancelable: true
      })
    );
  }, []);

  const handleSubmitForm = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (optionSelected.current) {
        const queryParams = new URLSearchParams({ placeId: String(optionSelected.current.value) });
        queryParams.append("vaccines", product);
        const targetUrl = `${finderLocation}?${queryParams.toString()}`;

        if (forceExitModalReason) {
          openExitModalForLink(targetUrl, forceExitModalReason);
          return;
        }

        // Delay route change slightly so analytics can attribute the submit event correctly.
        setTimeout(() => router.push(targetUrl), 100);
      }
    },
    [finderLocation, forceExitModalReason, openExitModalForLink, product, router]
  );

  const searchOptionSelect = React.useCallback(
    (option: DropdownOption) => {
      optionSelected.current = option;
      handleSubmit();
    },
    [handleSubmit]
  );

  const {
    handleOnBlur,
    handleSearchChange,
    handleSearchSubmit,
    handleSearchSuggestionClick,
    searchSuggestionsList,
    searchError
  } = useLocationSearch({
    clientGeolocationErrorMessages,
    detectLocationDropdownLabel,
    invalidLocationErrorMessage: input.error || "",
    searchOptionSelect,
    searchValue,
    searchValueSet
  });

  return (
    <Box
      ref={formRef}
      as="form"
      data-track="finder-search-bar"
      sx={{
        display: "flex",
        flexDirection: iconOnly
          ? "row"
          : ["column", "column", layout === "vertical" ? "column" : "row"],
        gap: iconOnly ? 0 : 2,
        position: "relative",
        width: "100%"
      }}
      onSubmit={handleSubmitForm}
    >
      <Box sx={{ width: "100%" }}>
        <TextField
          dropdownOptions={searchSuggestionsList}
          error={searchError}
          label={input.label}
          searchIcon={
            iconOnly
              ? () => (
                  <Button
                    aria-label={buttonTitle}
                    brand={brand}
                    icon={icon}
                    sx={buttonSx}
                    type="button"
                    variant={variant}
                    onClick={handleSearchSubmit}
                  />
                )
              : undefined
          }
          showMenuOverride={expandedMode}
          type="select"
          value={searchValue}
          onBlur={handleOnBlur}
          onChange={handleSearchChange}
          onEnter={handleSearchSubmit}
          onSelectOption={handleSearchSuggestionClick}
        />
      </Box>
      {!iconOnly ? (
        <Button
          brand={brand}
          icon={icon}
          size="lg"
          sx={buttonSx}
          variant={variant}
          onClick={handleSearchSubmit}
        >
          {buttonTitle}
        </Button>
      ) : null}
    </Box>
  );
};
